import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AddVisitModal from './AddVisitModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Sidebar from "../../components/Sidebar";
import TopNavbar from "../../components/TopNavbar";
import { getPatientDetails, getPatientVisits } from '../../services/patientService';
import { deleteVisit } from '../../services/visitService';
import { FaPlus, FaPrint, FaFilePdf, FaEdit, FaTrash } from "react-icons/fa";
import { Button, Card, CardContent, Typography, Grid, Divider } from '@mui/material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import EditVisitModal from "./EditVisitsModal";

// Function to convert new lines to <br />
// const formatTextWithNewLines = (text) => {
//     return text ? text.split('\n').map((str, index) => <span key={index}>{str}<br /></span>) : '';
// };

const PatientRecords = () => {
    const { id } = useParams(); // Get patient ID from URL
    const [patient, setPatient] = useState(null);
    const [visits, setVisits] = useState([]); // Store the list of visits
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null); // To track the visit being edited
    const [showEditVisitModal, setShowEditVisitModal] = useState(false); // For edit visit modal
    const [showDeleteModal, setShowDeleteModal] = useState(false); // For delete confirmation modal

    useEffect(() => {
        const fetchPatient = async () => {
            const response = await getPatientDetails(id);
            if (response.success && response.data) {
                setPatient(response.data);
            } else {
                console.error('Error fetching patient:', response.error || response.message || 'Unknown error');
            }
        };

        const fetchVisits = async () => {
            const response = await getPatientVisits(id); // Fetch patient visits
            if (response.success && response.data) {
                setVisits(Array.isArray(response.data) ? response.data : []);
            } else {
                console.error('Error fetching visits:', response.error || response.message || 'Unknown error');
                setVisits([]); // Set empty array on error
            }
        };

        fetchPatient();
        fetchVisits();
    }, [id]);

    // Update visits after a new visit is added
    const handleVisitAdded = (newVisit) => {
        setVisits((prevVisits) => [newVisit, ...prevVisits]);
    };

    // Update visits after a visit is edited
    const handleVisitEdited = (editedVisit) => {
        setVisits((prevVisits) =>
            prevVisits.map((visit) => (visit.id === editedVisit.id ? editedVisit : visit))
        );
    };

    // Delete visit
    const handleDeleteVisit = async () => {
        try {
            await deleteVisit(selectedVisit.id); // Delete visit from backend
            setVisits((prevVisits) => prevVisits.filter((visit) => visit.id !== selectedVisit.id)); // Update UI
            setShowDeleteModal(false); // Close the delete confirmation modal
        } catch (error) {
            console.error("Error deleting visit:", error);
        }
    };

    if (!patient) {
        return <p>Loading...</p>;
    }

    // Function to format the date
    const formatVisitDate = (date) => {
        return date ? format(new Date(date), 'dd.MM.yyyy') : 'No visits yet';
    };

    // Function to handle Print
// Function to handle Print
    const handlePrint = (visit) => {
        const printContent = `
    <div style="padding: 20px; font-family: Arial, sans-serif; position: relative; min-height: 90vh;">
        <h1>Patient: ${patient.name} ${patient.lastname}</h1>
        <p><strong>Visit Date:</strong> ${formatVisitDate(visit.visit_date)}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>Diagnose:</strong><br />${visit.diagnose ? visit.diagnose.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>ECHO Report:</strong><br />${visit.echo_report ? visit.echo_report.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>Treatment:</strong><br />${visit.treatment ? visit.treatment.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />

        <div style="position: absolute; bottom: 0; right: 0; font-weight: bold; margin: 20px;">
            Dr. Spec. Endocrinolog: Beqir Mahmudi
        </div>
    </div>
    `;
        const newWindow = window.open('', '', 'width=800,height=600');
        newWindow.document.write(`<html><body>${printContent}</body></html>`);
        newWindow.document.close();
        newWindow.print();
    };


    // Function to handle PDF generation
// Function to handle PDF generation
    const handlePDF = (visit) => {
        const doc = new jsPDF();
        let yPosition = 10;

        // Title
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(`Patient: ${patient.name} ${patient.lastname}`, 10, yPosition);

        // Visit Date
        yPosition += 10;
        doc.setFontSize(12);
        doc.setFont('Helvetica', 'normal');
        doc.text(`Visit Date: ${formatVisitDate(visit.visit_date)}`, 10, yPosition);

        // Add divider
        yPosition += 5;
        doc.setLineWidth(0.5);
        doc.line(10, yPosition, 200, yPosition);

        // Diagnose
        yPosition += 10;
        doc.text(`Diagnose:`, 10, yPosition);
        yPosition += 5;
        doc.setFont('Helvetica', 'normal');
        doc.text(visit.diagnose.split('\n'), 10, yPosition);

        // Add divider
        yPosition += 10 + (visit.diagnose.split('\n').length * 5); // Adjust for text length
        doc.line(10, yPosition, 200, yPosition);

        // ECHO Report
        yPosition += 10;
        doc.text(`ECHO Report:`, 10, yPosition);
        yPosition += 5;
        doc.text(visit.echo_report.split('\n'), 10, yPosition);

        // Add divider
        yPosition += 10 + (visit.echo_report.split('\n').length * 5);
        doc.line(10, yPosition, 200, yPosition);

        // Treatment
        yPosition += 10;
        doc.text(`Treatment:`, 10, yPosition);
        yPosition += 5;
        doc.text(visit.treatment.split('\n'), 10, yPosition);

        // Footer text at the bottom-right of the page
        doc.setFont('Helvetica', 'bold');
        doc.text('Dr. Spec. Endocrinolog: Beqir Mahmudi', 180, 280, { align: 'right' }); // Adjust position to bottom-right

        // Save the PDF
        doc.save(`${patient.name}_${patient.lastname}_Visit_${formatVisitDate(visit.visit_date)}.pdf`);
    };


    return (
        <div className="d-flex">
            <Sidebar />
            <div className="flex-grow-1">
                <TopNavbar />

                <div className={'container pt-5'}>
                    <div className="mb-4 px-3 d-flex justify-content-between align-items-center">
                        <h1>Patient Records</h1>
                        <Button variant="contained" color="primary" startIcon={<FaPlus />}
                                onClick={() => setShowVisitModal(true)}>
                            Add Visit
                        </Button>
                    </div>

                    {/* Patient Information */}
                    <div className="mb-4 px-3">
                        <h2>{`${patient.name || 'N/A'} ${patient.lastname || 'N/A'}`}</h2>
                        <p><strong className={"text-primary"}>Last Visit:</strong> {formatVisitDate(patient.last_visit)}</p>
                    </div>

                    {/* Add Visit Modal */}
                    <AddVisitModal
                        patientId={id}
                        open={showVisitModal}
                        onClose={() => setShowVisitModal(false)}
                        onVisitAdded={handleVisitAdded}
                    />

                    {/* Edit Visit Modal */}
                    <EditVisitModal
                        open={showEditVisitModal}
                        onClose={() => setShowEditVisitModal(false)}
                        visit={selectedVisit}
                        onVisitEdited={handleVisitEdited}
                    />

                    {/* Delete Confirmation Modal */}
                    <DeleteConfirmationModal
                        open={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleDeleteVisit}
                        visit={selectedVisit}
                    />

                    <Divider variant="middle" className={"border-primary"} />

                    {/* Display Previous Visits */}
                    <div className="my-4">
                        <Grid container spacing={2}>
                            {visits.length > 0 ? visits.map((visit) => (
                                <Grid item xs={12} sm={6} md={4} key={visit.id}>
                                    <Card className={"border shadow shadow-primary"}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom
                                                        className={"fw-bold border-bottom border-primary"}>
                                                Visit Date: <span className={"text-primary"}>{formatVisitDate(visit.visit_date)}</span>
                                            </Typography>

                                            {/* Diagnose */}
                                            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                                                <Typography variant="subtitle1" className={"fw-bold"} component="span">
                                                    Diagnose: 
                                                </Typography> <br />
                                                {visit.diagnose || 'No data available'}
                                            </Typography>

                                            <Divider variant="middle" className={"my-2"} />

                                            {/* Anamnesis */}
                                            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                                            <Typography variant="subtitle1" className={"fw-bold"} component="span">
                                                Anamnesis: 
                                            </Typography> <br /> 
                                            {visit.anamnesa || 'No data available'}
                                            </Typography>

                                            <Divider variant="middle" className={"my-2"} />

                                            {/* ECHO Report */}
                                            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                                                <Typography variant="subtitle1" className={"fw-bold"} component="span">
                                                    ECHO Report: 
                                                </Typography> <br />
                                                 {visit.echo_report || 'No data available'}
                                            </Typography>

                                            <Divider variant="middle" className={"my-2"} />

                                            {/* Treatment */}
                                            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                                                <Typography variant="subtitle1" className={"fw-bold"} component="span">
                                                    Treatment: 
                                                </Typography> <br />
                                                {visit.treatment || 'No data available'}
                                            </Typography>

                                            <Divider variant="middle" className={"my-2"} />

                                            <div className="mt-2 d-flex justify-content-between">
                                                <Button variant="outlined" startIcon={<FaPrint />}
                                                        onClick={() => handlePrint(visit)}>
                                                    Print
                                                </Button>
                                                <Button variant="outlined" startIcon={<FaFilePdf />}
                                                        onClick={() => handlePDF(visit)}>
                                                    PDF
                                                </Button>
                                                <Button variant="outlined" color="primary" startIcon={<FaEdit />}
                                                        onClick={() => {
                                                            setSelectedVisit(visit);
                                                            setShowEditVisitModal(true);
                                                        }}>
                                                    Edit
                                                </Button>
                                                <Button variant="outlined" color="secondary" startIcon={<FaTrash />}
                                                        onClick={() => {
                                                            setSelectedVisit(visit);
                                                            setShowDeleteModal(true);
                                                        }}>
                                                    Delete
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )) : (
                                <Typography variant="h6" className="text-center ms-4">No visits available</Typography>
                            )}
                        </Grid>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PatientRecords;
